// src/pages/RecoverPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import RecoveredPassCard from '../components/RecoveredPassCard';
import DigitalPassCard from '../components/DigitalPassCard';
import { recoverPasses } from '../services/ticketApi';
import { trackMetric } from '../services/telemetryService';

export default function RecoverPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState('');
  const [searchError, setSearchError] = useState(null);

  // Telemetría al montar la página de consulta
  useEffect(() => {
    trackMetric('page_view', { page: 'recover' });
  }, []);

  // Estado para modal / vista previa de pase individual seleccionado
  const [selectedPass, setSelectedPass] = useState(null);

  // Detección reactiva en tiempo real del tipo de entrada (teléfono vs correo)
  const getDetectionStatus = (input) => {
    const trimmed = input.trim();
    if (!trimmed) return null;

    const cleanDigits = trimmed.replace(/\D/g, '');

    // Detección de correo
    if (trimmed.includes('@')) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(trimmed)) {
        return {
          type: 'email',
          valid: true,
          label: 'Correo Electrónico',
          icon: 'bi-envelope-check-fill',
          badgeClass: 'bg-success text-white'
        };
      }
      return {
        type: 'email',
        valid: false,
        label: 'Correo Electrónico...',
        icon: 'bi-envelope',
        badgeClass: 'bg-secondary text-light'
      };
    }

    // Detección de teléfono solo dígitos
    if (/^\d+$/.test(cleanDigits)) {
      if (cleanDigits.length === 10) {
        return {
          type: 'phone',
          valid: true,
          label: 'Teléfono (10 dígitos)',
          icon: 'bi-telephone-fill',
          badgeClass: 'bg-success text-white'
        };
      }
      return {
        type: 'phone',
        valid: false,
        label: `${cleanDigits.length}/10 dígitos`,
        icon: 'bi-telephone',
        badgeClass: 'bg-secondary text-light'
      };
    }

    return null;
  };

  const detection = getDetectionStatus(query);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    const cleanQuery = query.trim();

    if (!cleanQuery) {
      setSearchError('Por favor ingresa tu teléfono a 10 dígitos o correo electrónico.');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSelectedPass(null);
    trackMetric('pass_lookup_search');

    const response = await recoverPasses(cleanQuery);
    setIsSearching(false);
    setHasSearched(true);

    if (response.success && response.found) {
      trackMetric('pass_lookup_success', { metadata: { count: response.passes?.length || 0 } });
      setSearchResults(response.passes || []);
      setSearchMessage(response.message);
    } else {
      trackMetric('pass_lookup_not_found');
      setSearchResults([]);
      setSearchMessage(response.message || 'No se encontraron boletos registrados con este teléfono o correo.');
    }
  };

  return (
    <div
      className="recover-page-wrapper py-4 py-md-5 position-relative overflow-hidden"
      style={{ minHeight: '88vh', background: 'transparent' }}
    >
      <div className="container position-relative" style={{ zIndex: 1 }}>
        {/* BOTÓN DE REGRESO ALINEADO CON EL ANCHO DEL FORMULARIO */}
        <div className="row justify-content-center mb-3">
          <div className="col-12 col-md-10 col-lg-7 col-xl-6">
            <Link
              to="/r"
              className="btn btn-xolo-outline rounded-circle d-inline-flex align-items-center justify-content-center shadow"
              style={{
                width: '42px',
                height: '42px',
                padding: 0
              }}
              aria-label="Regresar a Registro"
              title="Regresar a la página de registro"
            >
              <i className="bi bi-arrow-left fs-5"></i>
            </Link>
          </div>
        </div>

        {/* ENCABEZADO DE RECUPERACIÓN */}
        <div className="text-center mb-4">
          <div className="d-flex justify-content-center mb-3">
            <img
              src="/img/brand/logo-xolofest-horizontal.png"
              alt="Xolo Fest 2026"
              style={{
                maxHeight: '75px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'drop-shadow(0 6px 18px rgba(0, 0, 0, 0.6))'
              }}
            />
          </div>

          <h1 className="h2 text-white fw-black mb-2" style={{ textShadow: '0 4px 18px rgba(0, 0, 0, 0.95)' }}>
            Consulta y Descarga tus <span className="text-white" style={{ textShadow: '0 4px 20px rgba(0, 0, 0, 0.95), 0 0 20px rgba(220, 38, 38, 0.6)' }}>Pases Digitales</span>
          </h1>
          <p className="text-light opacity-90 mx-auto" style={{ maxWidth: '640px' }}>
            Ingresa el número de teléfono o correo electrónico que utilizaste al registrarte para consultar tus accesos.
          </p>
        </div>

        {/* CONTENEDOR PRINCIPAL */}
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-7 col-xl-6">
            <div
              className="ticket-form-card shadow-2xl mb-4 p-4 p-md-5 rounded-4"
              style={{ backgroundColor: '#111116', border: '1px solid rgba(220, 38, 38, 0.3)' }}
            >
              {/* BUSCADOR DE CAMPO ÚNICO LIMPIO */}
              <form onSubmit={handleSearch} noValidate className="mb-2">
                <div className="mb-4">
                  <label className="form-label text-light small fw-bold d-flex justify-content-between align-items-center">
                    <span>Escribe el teléfono o correo electrónico con el cual te registraste</span>
                    {detection && (
                      <span className={`badge ${detection.badgeClass} d-inline-flex align-items-center gap-1`}>
                        <i className={`bi ${detection.icon}`}></i>
                        {detection.label}
                      </span>
                    )}
                  </label>

                  <div className="input-group">
                    <span className="input-group-text bg-black border-secondary border-opacity-50 text-danger">
                      <i className={detection?.type === 'email' ? 'bi bi-envelope-fill' : 'bi bi-telephone-fill'}></i>
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-dark font-monospace"
                      placeholder="6641234567 o aficionado@ejemplo.com"
                      value={query}
                      onChange={(e) => {
                        setQuery(e.target.value);
                        setSearchError(null);
                      }}
                      disabled={isSearching}
                      autoFocus
                    />
                  </div>
                </div>

                {searchError && (
                  <div className="alert alert-danger border-danger border-opacity-50 bg-danger bg-opacity-10 text-light py-2 px-3 mb-3 small" role="alert">
                    <i className="bi bi-exclamation-circle-fill me-2 text-danger"></i>
                    {searchError}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-xolo-primary w-100 py-3 fw-bold fs-6 text-uppercase letter-spacing-md shadow-lg d-flex align-items-center justify-content-center gap-2"
                  disabled={isSearching || !query.trim()}
                >
                  {isSearching ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span>Consultando en el sistema...</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-search fs-6"></i>
                      <span>Buscar mis Pases</span>
                    </>
                  )}
                </button>
              </form>

              {/* ENLACE SECUNDARIO A REGISTRO */}
              <div className="text-center mt-4 pt-3 border-top border-secondary border-opacity-25">
                <span className="text-light opacity-90 small">¿Aún no has registrado tu boleto? </span>
                <Link to="/r" className="text-danger text-decoration-none small fw-bold">
                  Regístralo aquí
                </Link>
              </div>
            </div>

            {/* SECCIÓN DE RESULTADOS DE BÚSQUEDA */}
            {hasSearched && (
              <div className="search-results-section mt-4 animate-fade-in">
                {searchResults.length > 0 ? (
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-3 px-1">
                      <h3 className="h6 text-white text-uppercase fw-bold mb-0">
                        <i className="bi bi-collection-fill text-danger me-2"></i>
                        Boletos Encontrados ({searchResults.length})
                      </h3>
                      <span className="badge bg-success text-white border border-success fw-bold shadow-sm">
                        {searchResults.length === 1 ? '1 Pase Activo' : `${searchResults.length} Pases Activos`}
                      </span>
                    </div>

                    {/* LISTADO DE TARJETAS DE PASE RECUPERADO */}
                    <div className="passes-list">
                      {searchResults.map((passItem, index) => (
                        <RecoveredPassCard
                          key={passItem.ticketFolio || passItem.folio || index}
                          pass={passItem}
                          onViewPass={(selected) => setSelectedPass(selected)}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  /* MENSAJE DE NO ENCONTRADO CON SUGERENCIAS */
                  <div
                    className="not-found-card p-4 rounded-4 text-center"
                    style={{
                      backgroundColor: '#14141c',
                      border: '1px dashed rgba(255, 255, 255, 0.15)'
                    }}
                  >
                    <div className="not-found-icon mb-3">
                      <div
                        className="rounded-circle d-inline-flex align-items-center justify-content-center p-3"
                        style={{ backgroundColor: 'rgba(220, 38, 38, 0.12)' }}
                      >
                        <i className="bi bi-ticket-detailed text-danger fs-1"></i>
                      </div>
                    </div>

                    <h4 className="h5 text-white fw-bold mb-2">No se encontraron boletos registrados</h4>
                    <p className="text-light opacity-90 small mb-4 mx-auto" style={{ maxWidth: '420px' }}>
                      {searchMessage || 'Verifica que hayas ingresado el mismo número de teléfono a 10 dígitos o correo electrónico que utilizaste al registrarte.'}
                    </p>

                    <div className="d-flex flex-wrap justify-content-center gap-2">
                      <Link to="/r" className="btn btn-xolo-primary btn-sm px-3 py-2 fw-semibold">
                        <i className="bi bi-ticket-perforated-fill me-1"></i> Registrar Boleto Ahora
                      </Link>
                      <button
                        type="button"
                        className="btn btn-xolo-outline btn-sm px-3 py-2"
                        onClick={() => {
                          setQuery('');
                          setHasSearched(false);
                          setSearchResults([]);
                        }}
                      >
                        Intentar con otro dato
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* VISTA PREVIA DETALLADA / MODAL DE PASE INDIVIDUAL SELECCIONADO */}
            {selectedPass && (
              <div className="selected-pass-modal-wrapper mt-4 animate-fade-in">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h3 className="h6 text-danger text-uppercase fw-bold mb-0">
                    <i className="bi bi-qr-code me-2"></i> Vista Detallada de Pase
                  </h3>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary text-light"
                    onClick={() => setSelectedPass(null)}
                  >
                    <i className="bi bi-x-lg me-1"></i> Cerrar Vista
                  </button>
                </div>

                <DigitalPassCard
                  passData={selectedPass}
                  isRecovered={true}
                  onReset={() => navigate('/r')}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
